import { useState, useEffect } from 'react';
import {Link} from "react-router-dom";
import React from 'react';

import ActionsButton from "../../../components/ui/ActionsButton/ActionsButton";
import {getRafOverrun, handleFilterList, useFilterQuery, frenchFormat, generateDateLabels} from "../../../utils";
import { DealActions } from '../../../actions';
import { SelectInput, TableDatePicker } from '../../../components/ui/Table';
import Badge from '../../../components/ui/Badge';
import {Tooltip} from 'antd';
import { UserState } from '../../../reducers/UserReducer';
import { Deal } from '../../../models/deal/deal';

export const currentMissionsTableConfig = (
    tableEdit: boolean,
    filterMissionList: (item: string, tableType: string | null) => void,
    userData: UserState['userProfileData']
) => {
    const [deals, setDeals] = useState<Deal[]>([]);
    const [filterQuery, setFilterQuery] = useState<Record<string, string>>({
        deal: '',
        mission_end_date_before: '',
        mission_end_date_after: ''
    });

    const dateLabels = generateDateLabels();

    const handleFilter = (value: string |string[], name: string) => {
        handleFilterList(value, name, filterQuery, setFilterQuery);
    };

    useFilterQuery(filterQuery, filterMissionList);

    const handleGetDeals = async () => {
        const result = await DealActions.getAllDeals(userData, `&type=business&display=${userData?.userProfileName}`);

        if (!result.success) {return setDeals([])}

        setDeals(result.data);

    };

    useEffect(() => {
        // ↓ users dropdown values
        (async () => {
            await handleGetDeals();
        })
    }, []);

    return (
        {
            dropdown: false,
            dropdownLeftPadding: true,
            greyBackground: false,
            subline: true,
            columnConfig: [
                {
                    label: 'Affaire',
                    value: 'deal.short_name',
                    width: 'w-[15%]',
                    sort: true,
                    component: (item: any) => {
                        return (<div style={{fontFamily: 'Roboto'}} className="flex flex-row items-center justify-start w-full">
                            {`[${item.deal.customer?.name}] ${item.deal.parent ? `(${item.deal.parent.short_name})` : ''} ${item.deal.short_name}`}
                        </div>)}

                    ,
                    filterComponent: () => <SelectInput
                        name="deal"
                        options={deals}
                        isSearchable={true}
                        onChange={(value: any) => handleFilter(value, 'deal')}
                        fetchFunction={(handleGetDeals)}
                        labelKeys={['short_name']}
                        valueKey={'id'}
                    />,
                    sumDisplayed: false,
                    sumBy: '',
                    sumLabel: ''
                },
                {
                    label: 'Resp. Production',
                    value: 'production_lead.username',
                    width: 'w-[6%]',
                    sort: false,
                    component: (item: any) =>  (
                        <div className="flex flex-row items-center justify-center">
                            {
                                item.deal.production_lead ?
                                    <Badge content={[item.deal.production_lead]} type='rounded'/> :
                                    <span className='text-gray-500'>-</span>
                            }
                        </div>
                    )
                    ,
                    sumDisplayed: false,
                    sumBy: '',
                    sumLabel: ''
                },
                {
                    label: 'Jours',
                    value: 'billable_days',
                    width: 'w-[7%]',
                    sort: true,
                    component: (item: any) => (
                        <div className="flex flex-row items-center justify-center">
                            {item.billable_days ? +item.billable_days : 0}
                        </div>
                    ),
                    sumDisplayed: true,
                    sumBy: 'value',
                    sumLabel: 'jours'
                },
                {
                    label: 'Jrs. produits facturables',
                    value: 'billed_days',
                    width: 'w-[8%]',
                    sort: true,
                    component: (item: any) => (
                        <div className="flex flex-row items-center justify-center">
                            {item.billed_days ? +item.billed_days : 0}
                        </div>
                    ),
                    sumDisplayed: true,
                    sumBy: 'value',
                    sumLabel: 'jours'
                },
                {
                    label: 'Jrs. produits non fact...',
                    tooltipLabel: 'Jours produits non facturables',
                    value: 'non_billable_days',
                    width: 'w-[8%]',
                    sort: true,
                    component: (item: any) => {
                        return (
                            <div className="flex flex-row items-center justify-center">
                                {+item.non_billable_days || 0}
                            </div>
                        )},
                    sumDisplayed: true,
                    sumBy: 'value',
                    sumLabel: 'jours'
                },
                {
                    label: 'RAF',
                    value: 'raf',
                    width: 'w-[8%]',
                    sort: true,
                    component: (item: any) => (
                        <div className="flex flex-row items-center justify-center">
                            {item.raf ? +item.raf : '0'}
                        </div>
                    ),
                    sumDisplayed: true,
                    sumBy: 'value',
                    sumLabel: 'jours'
                },
                {
                    label: dateLabels.currentMonthLabel,
                    value: 'projection_m1',
                    key: 'projection_m1',
                    width: 'w-[6%]',
                    sort: true,
                    component: (item: any, onChange: any, value: any) => {
                        const overrun = getRafOverrun(item, 'projection_m1');

                        return (
                            <>
                                {
                                    tableEdit ?
                                        <div className="flex flex-row items-center justify-center px-3 text-center">
                                            <input
                                                type='number'
                                                value={value}
                                                onChange={onChange}
                                                className='w-full px-2 py-1 rounded-sm border-[1px] border-[#d9d9d9] focus:outline-none focus:border-[#ea8a3d]'
                                            />
                                        </div> :
                                        <div className={"flex flex-row items-center justify-center text-center"}>
                                            <Tooltip title={'Réalisé'}>
                                                <p>{+item?.produced_days_m1}</p>
                                            </Tooltip>
                                            <p className='mx-0.5'>/</p>
                                            <Tooltip title={'Projection'}>
                                                <p className={overrun}>
                                                    {+item.projection_m1}
                                                </p>
                                            </Tooltip>
                                        </div>
                                }
                            </>
                        );
                    },
                    sumDisplayed: true,
                    sumBy: 'value',
                    sumLabel: 'jours'
                },
                {
                    label: dateLabels.nextMonthLabel,
                    value: 'projection_m2',
                    key: 'projection_m2',
                    width: 'w-[6%]',
                    sort: true,
                    component: (item: any, onChange: any, value: any) => {
                        const overrun = getRafOverrun(item, 'projection_m2');

                        return (
                            <>
                                {
                                    tableEdit ?
                                        <div className="flex flex-row items-center justify-center px-3 text-center">
                                            <input
                                                type='number'
                                                value={value}
                                                onChange={onChange}
                                                className='w-full px-2 py-1 rounded-sm border-[1px] border-[#d9d9d9] focus:outline-none focus:border-[#ea8a3d]'
                                            />
                                        </div> :
                                        <div className={`flex flex-row items-center justify-center text-center ${overrun}`}>
                                            {+item.projection_m2}
                                        </div>
                                }
                            </>
                        );
                    },
                    sumDisplayed: true,
                    sumBy: 'value',
                    sumLabel: 'jours'
                },
                {
                    label: dateLabels.nextTwoMonthLabel,
                    value: 'projection_m3',
                    key: 'projection_m3',
                    width: 'w-[6%]',
                    sort: true,
                    component: (item: any, onChange: any, value: any) => {
                        const overrun = getRafOverrun(item, 'projection_m3');

                        return (
                            <>
                                {
                                    tableEdit ?
                                        <div className="flex flex-row items-center justify-center px-3 text-center">
                                            <input
                                                type='number'
                                                value={value}
                                                onChange={onChange}
                                                className='w-full px-2 py-1 rounded-sm border-[1px] border-[#d9d9d9] focus:outline-none focus:border-[#ea8a3d]'
                                            />
                                        </div> :
                                        <div className={`flex flex-row items-center justify-center text-center ${overrun}`}>
                                            {+item.projection_m3}
                                        </div>
                                }
                            </>
                        );
                    },
                    sumDisplayed: true,
                    sumBy: 'value',
                    sumLabel: 'jours'
                },
                {
                    label: dateLabels.trimesterLabel,
                    value: 'projection_q1',
                    key: 'projection_q1',
                    width: 'w-[6%]',
                    sort: true,
                    component: (item: any, onChange: any, value: any) => {
                        const overrun = getRafOverrun(item, 'projection_q1');

                        return (
                            <>
                                {
                                    tableEdit ?
                                        <div className="flex flex-row items-center justify-center px-3 text-center">
                                            <input
                                                type='number'
                                                value={value}
                                                onChange={onChange}
                                                className='w-full px-2 py-1 rounded-sm border-[1px] border-[#d9d9d9] focus:outline-none focus:border-[#ea8a3d]'
                                            />
                                        </div> :
                                        <div className={`flex flex-row items-center justify-center text-center ${overrun}`}>
                                            {+item.projection_q1}
                                        </div>
                                }
                            </>
                        );
                    },
                    sumDisplayed: true,
                    sumBy: 'value',
                    sumLabel: 'jours'
                },
                {
                    label: dateLabels.semesterLabel,
                    value: 'projection_s1',
                    key: 'projection_s1',
                    width: 'w-[6%]',
                    sort: true,
                    component: (item: any, onChange: any, value: any) => {
                        const overrun = getRafOverrun(item, 'projection_s1');

                        return (
                            <>
                                {
                                    tableEdit ?
                                        <div className="flex flex-row items-center justify-center px-3 text-center">
                                            <input
                                                type='number'
                                                value={value}
                                                onChange={onChange}
                                                className='w-full px-2 py-1 rounded-sm border-[1px] border-[#d9d9d9] focus:outline-none focus:border-[#ea8a3d]'
                                            />
                                        </div> :
                                        <div className={`flex flex-row items-center justify-center text-center ${overrun}`}>
                                            {+item.projection_s1}
                                        </div>
                                }
                            </>
                        );
                    },
                    sumDisplayed: true,
                    sumBy: 'value',
                    sumLabel: 'jours'
                },
                {
                    label: 'Date de fin',
                    value: null,
                    width: 'w-[6%]',
                    sort: true,
                    component: (item: any) =>  (
                        <div className="flex flex-row items-center justify-center">
                            {item.deal.mission_end_date ? frenchFormat(item.deal.mission_end_date) : '-'}
                        </div>
                    )
                    ,
                    filterComponent: () => (
                        <TableDatePicker name='mission_end_date' onChange={(value: any) => handleFilter(value, 'mission_end_date')}/>
                    ),
                    sumDisplayed: false,
                    sumBy: '',
                    sumLabel: ''
                },
                {
                    label: 'Détails',
                    value: null,
                    width: 'w-[4%]',
                    sort: false,
                    component: (item: any) => (
                        <div className="flex flex-row items-center justify-center">
                            <Link to={`/deals/${item.deal?.id}/missions/${item.id}`} id='skip' className='ml-1'>
                                <ActionsButton type="details"/>
                            </Link>
                        </div>
                    ),
                    sumDisplayed: false,
                    sumBy: '',
                    sumLabel: ''
                }
            ]
        }
    );
};
